import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export type BadgeVariant = 'neutral' | 'priority' | 'status' | 'sla';
export type Priority = 'critico' | 'alto' | 'medio' | 'baixo';
export type Status =
  | 'aberto'
  | 'em_andamento'
  | 'em_espera'
  | 'resolvido'
  | 'cancelado'
  | 'fechado'
  | 'reaberto';
export type SlaState = 'ok' | 'warning' | 'danger' | 'breach';

const PRIORITY: Record<Priority, string> = {
  critico: 'bg-priority-critico text-white',
  alto: 'bg-priority-alto text-white',
  medio: 'bg-priority-medio text-white',
  baixo: 'bg-priority-baixo text-white',
};

const STATUS: Record<Status, string> = {
  aberto: 'bg-status-aberto text-white',
  em_andamento: 'bg-status-em_andamento text-white',
  em_espera: 'bg-status-em_espera text-yhelp-black',
  resolvido: 'bg-status-resolvido text-yhelp-black',
  cancelado: 'bg-status-cancelado text-white',
  fechado: 'bg-status-fechado text-white',
  reaberto: 'bg-status-reaberto text-white',
};

const SLA: Record<SlaState, string> = {
  ok: 'bg-sla-ok text-yhelp-black',
  warning: 'bg-sla-warning text-yhelp-black',
  danger: 'bg-sla-danger text-white',
  breach: 'bg-sla-breach text-white',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  priority?: Priority;
  status?: Status;
  sla?: SlaState;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'neutral', priority, status, sla, className, children, ...rest },
  ref,
) {
  let palette = 'bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-100';
  if (variant === 'priority' && priority) palette = PRIORITY[priority];
  if (variant === 'status' && status) palette = STATUS[status];
  if (variant === 'sla' && sla) palette = SLA[sla];
  return (
    <span
      ref={ref}
      role={variant === 'sla' ? 'status' : undefined}
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide',
        palette,
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
});
